/**
 * Drives the post-provider redirect logic for the /oauth-native-callback screen.
 *
 * Owns three effects:
 *  - Native: sets the Supabase session from the deep-link URL (implicit or PKCE flow).
 *  - Web error: surfaces + logs provider/auth-server failures appended as `?error=...`.
 *  - Success / grace: routes a signed-in user into /app, or back to /sign-in once the
 *    grace window closes without a session.
 *
 * Returns only what the screen needs to render its loading modal.
 */

import { useEffect, useRef } from 'react'
import { Platform, Linking } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { useAnalytics } from '../../analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../analytics'
import {
  ERROR_MESSAGES,
  OAUTH_FLOW_STORAGE_KEY,
  OAUTH_POST_AUTH_PATH_STORAGE_KEY,
} from '../constants/oauth.constants'
import { resolveOAuthCallbackBannerMessage, isOAuthCodeReuseFailure } from '../helpers/oauthCallbackError.helper'
import { attemptSilentOAuthAutoRetry, clearOAuthAutoRetryState } from '../helpers/oauthAutoRetry.helper'
import { establishWebAuthSessionFromCallbackUrl } from '../helpers/authWebCallbackSession.helper'
import { exchangeOAuthCodeForSessionOnce } from '../helpers/oauthPkceExchange.helper'
import { useOAuthFlow } from './useOAuthFlow'
import { parseSupabaseOAuthRedirectUrl } from '../helpers/signInApi'
import {
  getSafePostAuthPath,
  POST_AUTH_DEFAULT_PATH,
} from '../helpers/postAuthRedirect.helper'
import { getPendingB2bJoinCode } from '../../join/helpers/pendingB2bJoin.helper'
import { extractSquadJoinFromCallbackSearch } from '../../join/helpers/pendingWcSquadJoin.helper'
import { competitionBasePath, competitionGroupJoinRoute } from '../../competitions/helpers/competitionRoutes.helper'
import { supabase } from '../../lib/supabase'
import { useAccountDeletionGateBeforeAppEntry } from './useAccountDeletionGateBeforeAppEntry'
import { flushOAuthWebSignupConsentsIfPending } from '../helpers/oauthWebSignupConsentFlush.helper'
import { setPendingSignInBannerMessage } from '../helpers/signInBannerTransfer.helper'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../helpers/authLiveDebug.helper'
import { getAuthUserObservabilityFields } from '../../shared/helpers/authObservability.helper'

interface UseOAuthCallbackRedirectReturn {
  isProcessing: boolean
  loadingMessage: string
  isLoaded: boolean
}

export function useOAuthCallbackRedirect(): UseOAuthCallbackRedirectReturn {
  const router = useRouter()
  const { track } = useAnalytics()
  const { isLoaded, isSignedIn, session } = useSupabaseAuth()
  const appEntryPhase = useAccountDeletionGateBeforeAppEntry()
  const { isProcessing, loadingMessage, setIsProcessing } = useOAuthFlow()
  const hasRedirected = useRef(false)
  const hasTrackedOAuth = useRef(false)
  const hasStartedWebSessionEstablish = useRef(false)
  const hasStartedNativePkceExchange = useRef(false)
  const mountedAt = useRef(Date.now())
  // Captured at mount, before session-establish runs — the success effect reads this rather than
  // window.location.search, which a post-auth history.replaceState could strip out from under it.
  const wcJoinCodeRef = useRef<{ competitionId: string; inviteCode: string } | null>(null)

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Cross-app handoff fallback: the WC join code rides the OAuth redirect URL. Read it now,
      // while the query string is still intact (see wcJoinCodeRef note above).
      wcJoinCodeRef.current = extractSquadJoinFromCallbackSearch(window.location.search)
    }
    authLiveDebug('OAuthCallback mounted', {
      platform: Platform.OS,
      hasCode: Platform.OS === 'web' ? window.location.href.includes('code=') : undefined,
      hasAccessToken: Platform.OS === 'web' ? window.location.href.includes('access_token=') : undefined,
      hasError: Platform.OS === 'web' ? window.location.search.includes('error=') : undefined,
      hashLen: Platform.OS === 'web' ? window.location.hash.length : undefined,
    })
    logger.warn('[OAuthCallback] mounted', {
      platform: Platform.OS,
      hasCode: Platform.OS === 'web' ? window.location.href.includes('code=') : undefined,
      hasAccessToken: Platform.OS === 'web' ? window.location.href.includes('access_token=') : undefined,
      hasError: Platform.OS === 'web' ? window.location.search.includes('error=') : undefined,
    })
  }, [])

  // Web: explicit session from callback URL (detectSessionInUrl is off). auth-js default is implicit
  // (#access_token=); PKCE (?code=) is also supported via establishWebAuthSessionFromCallbackUrl.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    if (hasStartedWebSessionEstablish.current) return

    const queryParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    if (queryParams.get('error') || hashParams.get('error')) return

    const href = window.location.href
    const hasPkceCode = href.includes('code=')
    const hasImplicitTokens = href.includes('access_token=')
    if (!hasPkceCode && !hasImplicitTokens) return

    hasStartedWebSessionEstablish.current = true

    void (async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        authLiveDebug('OAuthCallback web — session already present, skip establish')
        return
      }

      authLiveDebug('OAuthCallback web — establishWebAuthSessionFromCallbackUrl', {
        hasPkceCode,
        hasImplicitTokens,
      })
      const result = await establishWebAuthSessionFromCallbackUrl(href)
      authLiveDebug('OAuthCallback web establishWebAuthSessionFromCallbackUrl result', {
        ok: result.ok,
        skipped: result.skipped,
      })
      if (!result.ok && !result.skipped) {
        logger.error('[OAuthCallback] web establishWebAuthSessionFromCallbackUrl failed')
      }
    })()
  }, [])

  useEffect(() => {
    authLiveDebug('OAuthCallback auth snapshot', {
      isLoaded,
      isSignedIn,
      appEntryPhase,
      isProcessing,
      msSinceMount: Date.now() - mountedAt.current,
      ...getAuthUserObservabilityFields(session?.user ?? null),
    })
  }, [isLoaded, isSignedIn, appEntryPhase, isProcessing, session?.user])

  // Native: parse OAuth redirect URL and set session when app is opened via deep link
  useEffect(() => {
    if (Platform.OS === 'web') return

    const trySetSessionFromUrl = async (url: string | null) => {
      authLiveDebug('OAuthCallback native trySetSessionFromUrl', {
        hasUrl: !!url,
        hasCode: url?.includes('code=') ?? false,
        hasHash: url?.includes('#') ?? false,
      })
      if (!url) return
      // Implicit flow: tokens in hash fragment
      const tokens = parseSupabaseOAuthRedirectUrl(url)
      if (tokens) {
        authLiveDebug('OAuthCallback native implicit flow — setSession')
        const { error } = await supabase.auth.setSession(tokens)
        authLiveDebug('OAuthCallback native setSession result', { ok: !error, message: error?.message })
        return
      }
      // PKCE: auth code in query string (when flowType is pkce)
      if (url.includes('code=')) {
        if (hasStartedNativePkceExchange.current) {
          authLiveDebug('OAuthCallback native PKCE — exchange already started, skip')
          return
        }
        hasStartedNativePkceExchange.current = true
        authLiveDebug('OAuthCallback native PKCE — exchangeCodeForSessionOnce')
        const result = await exchangeOAuthCodeForSessionOnce(url)
        authLiveDebug('OAuthCallback native exchangeCodeForSessionOnce result', {
          ok: result.ok,
          skipped: result.skipped,
          message: result.error?.message,
        })
      }
    }

    void Linking.getInitialURL().then(trySetSessionFromUrl)

    const sub = Linking.addEventListener('url', ({ url }) => {
      void trySetSessionFromUrl(url)
    })
    return () => sub.remove()
  }, [])

  // Web: Supabase appends `?error=...&error_description=...` to this callback URL when the
  // provider/auth-server leg fails (e.g. GoTrue 500 "Unable to exchange external code", or a
  // cancelled consent). Without this, those failures look identical to a slow load: the user
  // silently bounces to /sign-in with no message and nothing logged. Surface + log them.
  useEffect(() => {
    if (Platform.OS !== 'web' || hasRedirected.current || typeof window === 'undefined') return
    // Need the auth state resolved before deciding: an error param on the URL is only fatal if we
    // don't already hold a session (see the stale-re-hit guard below).
    if (!isLoaded) return

    const queryParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const error = queryParams.get('error') || hashParams.get('error')
    if (!error) return

    // Stale re-hit: after a successful sign-in, the browser can re-request this callback (back-nav,
    // bfcache restore, or invite-link prefetch) with the now-consumed Google code still in flight.
    // GoTrue rejects the reused external code ("Unable to exchange external code" → invalid_grant)
    // and 302s back here with ?error=... — but we already issued a token and have a live session.
    // Treating that as a failure is the "first login fails, retry works" bug: it bounces a
    // signed-in user to /sign-in with a scary banner. If a session exists, ignore the stale error
    // and let the success effect below route to /app.
    if (isSignedIn) {
      authLiveDebug('OAuthCallback web error IGNORED — stale re-hit, session exists', { error })
      logger.warn('OAuth callback error param ignored — session already established (stale re-hit)', {
        error,
      })
      return
    }

    const errorCode = queryParams.get('error_code') || hashParams.get('error_code')
    const errorDescription =
      queryParams.get('error_description') || hashParams.get('error_description')

    void (async () => {
      const retried = await attemptSilentOAuthAutoRetry(
        error,
        errorCode,
        errorDescription,
        isOAuthCodeReuseFailure,
      )
      if (retried) {
        authLiveDebug('OAuthCallback silent auto-retry — redirecting to provider')
        return
      }

      hasRedirected.current = true
      authLiveDebug('OAuthCallback web provider ERROR — redirecting to sign-in', {
        error,
        errorCode,
      })
      logger.error('OAuth callback returned a provider error', { error, errorCode, errorDescription })

      const friendly = ERROR_MESSAGES[error]
      // Don't nag the user with a banner when they simply cancelled the provider consent.
      if (!friendly?.isCancellation) {
        setPendingSignInBannerMessage(
          resolveOAuthCallbackBannerMessage({ error, errorCode, errorDescription }),
        )
      }
      setIsProcessing(false)
      router.replace('/sign-in')
    })()
  }, [router, setIsProcessing, isLoaded, isSignedIn])

  useEffect(() => {
    if (hasRedirected.current) return
    if (!isLoaded) return

    const ms = Date.now() - mountedAt.current

    if (isSignedIn && appEntryPhase === 'allow_redirect') {
      authLiveDebug('OAuthCallback SUCCESS — routing to app', {
        ms,
        platform: Platform.OS,
        appEntryPhase,
        ...getAuthUserObservabilityFields(session?.user ?? null),
      })
      logger.info('[OAuthCallback] session ready + allow_redirect → routing to app', {
        ms, platform: Platform.OS,
      })
      hasRedirected.current = true
      clearOAuthAutoRetryState()

      const completeRedirect = async () => {
        let nextPath: string = POST_AUTH_DEFAULT_PATH
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
          try {
            const raw = sessionStorage.getItem(OAUTH_POST_AUTH_PATH_STORAGE_KEY)
            sessionStorage.removeItem(OAUTH_POST_AUTH_PATH_STORAGE_KEY)
            if (raw) {
              nextPath = getSafePostAuthPath(raw)
            }
          } catch {
            nextPath = POST_AUTH_DEFAULT_PATH
          }
        }
        if (nextPath === POST_AUTH_DEFAULT_PATH && wcJoinCodeRef.current) {
          // sessionStorage was lost — the invite link opened in an in-app browser (WhatsApp) and
          // OAuth completed in a different browser context (system Safari). The join code carried
          // on the callback URL survives that cross-app hop; route straight to the auto-join screen
          // of the competition the code belongs to (WC when the URL carried no competition).
          const { competitionId, inviteCode } = wcJoinCodeRef.current
          nextPath = competitionGroupJoinRoute(competitionBasePath(competitionId), {
            code: inviteCode,
            autojoin: true,
          })
        }
        if (nextPath === POST_AUTH_DEFAULT_PATH) {
          const pendingJoinCode = await getPendingB2bJoinCode()
          if (pendingJoinCode) {
            nextPath = `/join?join=${encodeURIComponent(pendingJoinCode)}`
          }
        }
        authLiveDebug('OAuthCallback router.replace', { nextPath })
        router.replace(nextPath as Href)
      }

      const run = async () => {
        if (Platform.OS === 'web') {
          try {
            await flushOAuthWebSignupConsentsIfPending()
          } catch {
            // Non-blocking: user can fix preferences in Privacy controls.
          }
        }
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined' && !hasTrackedOAuth.current) {
          hasTrackedOAuth.current = true
          try {
            const flow = sessionStorage.getItem(OAUTH_FLOW_STORAGE_KEY)
            const wasGuest = sessionStorage.getItem('zporti_oauth_was_guest')
            sessionStorage.removeItem(OAUTH_FLOW_STORAGE_KEY)
            sessionStorage.removeItem('zporti_oauth_was_guest')
            if (wasGuest) {
              track(ANALYTICS_EVENTS.GUEST_CONVERTED, { method: 'oauth' })
            }
            if (flow === 'sign_up') {
              track(ANALYTICS_EVENTS.SIGN_UP_COMPLETED)
            } else {
              track(ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
            }
          } catch {
            track(ANALYTICS_EVENTS.SIGN_IN_COMPLETED)
          }
        }
        await completeRedirect()
      }

      void run()
      return
    }

    if (isSignedIn) {
      authLiveDebug('OAuthCallback signed in — waiting for deletion gate', {
        appEntryPhase,
        ms,
        platform: Platform.OS,
      })
      logger.info('[OAuthCallback] session exists, waiting for appEntryPhase', {
        appEntryPhase, ms, platform: Platform.OS,
      })
      return
    }

    // Not signed in yet. Defer to useOAuthFlow's grace window (`isProcessing`): it clears the
    // instant a session appears, and otherwise after ~5s — long enough for a cold-load PKCE code
    // exchange and aligned with the auth provider's own 5s init timeout. The previous hard-coded
    // 3s timer bounced users out before the exchange could finish on slow networks.
    if (!isProcessing) {
      hasRedirected.current = true
      authLiveDebug('OAuthCallback BOUNCE to sign-in — grace window expired, no session', {
        ms,
        platform: Platform.OS,
        isLoaded,
        isSignedIn,
        appEntryPhase,
      })
      logger.warn('[OAuthCallback] no session after grace window — returning to sign-in', {
        ms, platform: Platform.OS,
      })
      router.replace('/sign-in')
    }
  }, [isLoaded, isSignedIn, appEntryPhase, isProcessing, router, track])

  return { isProcessing, loadingMessage, isLoaded }
}
